import React from "react";
import { CalendarClock, LayoutGrid, Users2, Repeat2, Hash, StickyNote, History, LogOut, ClipboardList, ClipboardCheck, ShieldCheck, X, AlertTriangle, CalendarDays, UserCircle2, MailPlus, Wheat, LayoutDashboard, PhoneCall, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFacility } from "@/context/FacilityContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const managerNav = [
  { id: "schedule",    label: "Schedule",        icon: LayoutGrid },
  { id: "shift-farm",  label: "Shift Farm",      icon: Wheat, fun: true },
  { id: "nso",         label: "New Schedule Open Shifts", icon: Sparkles, nsoOnly: true },
  { id: "requests",    label: "Requests",        icon: ClipboardList },
  { id: "meetings",    label: "Meeting Manager", icon: Users2, adminOnly: true },
  { id: "employees",   label: "Employees",       icon: Users2 },
  { id: "availability",label: "Availability",    icon: CalendarDays },
  { id: "shift-codes", label: "Shift Codes",     icon: Hash },
  { id: "master",      label: "Master Schedule", icon: Repeat2 },
  { id: "oncall",      label: "On-Call Schedule",icon: PhoneCall },
  { id: "attendance",  label: "Attendance",      icon: ClipboardCheck },
  { id: "ot-rules",    label: "OT Rules",        icon: AlertTriangle },
  { id: "sched-posting", label: "Schedule Posting", icon: ShieldCheck, adminOnly: true },
  { id: "templates",   label: "Message Templates", icon: MailPlus, adminOnly: true },
  { id: "notes",       label: "Notes",           icon: StickyNote },
  { id: "audit",       label: "Audit History",   icon: History },
  { id: "my-info",     label: "My Info",         icon: UserCircle2 },
];
const employeeNav = [
  { id: "my-dashboard", label: "My Dashboard", icon: LayoutDashboard },
  { id: "schedule",     label: "My Schedule",  icon: LayoutGrid },
  { id: "shift-farm",   label: "Shift Farm",   icon: Wheat, fun: true },
  { id: "nso",          label: "New Schedule Open Shifts", icon: Sparkles, nsoOnly: true },
  { id: "my-requests",  label: "My Requests",  icon: ClipboardList },
  { id: "availability", label: "Availability", icon: CalendarDays },
  { id: "notes",        label: "Notes",        icon: StickyNote },
  { id: "my-info",      label: "My Info",      icon: UserCircle2 },
];

export default function Sidebar({ active, onChange, open = false, onClose, badges = {} }) {
  const { user, logout } = useAuth();
  const { facilityId } = useFacility();
  const baseNav = user?.role === "employee" ? employeeNav : managerNav;
  const rosterFacs = user?.roster_facility_ids || user?.facility_ids || [];
  const unionAccess = rosterFacs.includes("auburn") || rosterFacs.includes("solano");
  // Employees at a SECONDARY facility (i.e. current facility ≠ their primary)
  // lose access to primary-only tools: Shift Farm and NSO. Secondary is
  // strictly view-schedule + pick-up-open-shifts. Legacy accounts without a
  // primary aren't gated so nothing breaks silently.
  const atSecondary = user?.role === "employee"
    && !!user?.primary_facility_id
    && !!facilityId
    && facilityId !== user.primary_facility_id;
  // NSO tab: employees must have `nso_participant`; managers/admins with roster
  // access to Auburn or Solano see it automatically. Employees also need to
  // be at their primary (NSO submissions target their primary facility).
  const nsoVisible = user?.role === "employee"
    ? (!!user?.nso_participant && unionAccess && !atSecondary)
    : unionAccess;
  const nav = (user?.role === "employee" && !user?.availability_required)
    ? baseNav.filter(n => n.id !== "availability")
    : baseNav.filter(n => !n.adminOnly || user?.role === "admin");
  // Hide Shift Farm at secondary — the game state is anchored to the
  // employee's primary facility so surfacing it at a secondary would
  // confuse both plant/harvest expectations and the leaderboard.
  // Also hide Availability + My Requests since both are primary-only
  // submit surfaces — showing them at a secondary just leads to 403s.
  const secondaryHidden = new Set(["shift-farm", "availability", "my-requests", "requests"]);
  const navFinal = nav
    .filter(n => !n.nsoOnly || nsoVisible)
    .filter(n => !(atSecondary && secondaryHidden.has(n.id)));
  const roleBadge =
    user?.role === "admin" ? { label: "Admin", cls: "bg-amber-100 text-amber-800" } :
    user?.role === "manager" ? { label: "Manager", cls: "bg-blue-100 text-blue-800" } :
    { label: "Employee", cls: "bg-slate-100 text-slate-700" };

  const pick = (id) => { onChange(id); onClose?.(); };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-40 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed lg:static top-0 left-0 z-50 w-64 lg:w-60 h-screen shrink-0 border-r border-slate-200 bg-white flex flex-col
                    transform transition-transform duration-200 ease-out
                    ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="p-4 lg:p-5 border-b border-slate-200 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600 grid place-items-center text-white shrink-0">
            <CalendarClock size={16} />
          </div>
          <div className="font-display text-base lg:text-lg font-semibold tracking-tight flex-1 min-w-0 truncate">
            Patient Access
          </div>
          <button
            className="lg:hidden text-slate-500 hover:text-slate-800 p-1"
            onClick={onClose}
            aria-label="Close menu"
            data-testid="sidebar-close"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {navFinal.map((n) => {
            const badge = badges[n.id];
            return (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => pick(n.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  active === n.id
                    ? (n.fun
                       ? "bg-gradient-to-r from-amber-100 via-lime-100 to-emerald-100 text-emerald-900 font-semibold ring-1 ring-amber-300"
                       : "bg-blue-50 text-blue-700 font-medium")
                    : (n.fun
                       ? "text-emerald-800 hover:bg-gradient-to-r hover:from-amber-50 hover:to-emerald-50 font-medium"
                       : "text-slate-600 hover:bg-slate-100")
                }`}
              >
                <n.icon size={16} className={n.fun ? "text-amber-600 animate-pulse" : ""} />
                <span className="flex-1 text-left">{n.label}</span>
                {badge > 0 && (
                  <span
                    data-testid={`nav-badge-${n.id}`}
                    className="ml-auto text-[10px] font-semibold rounded-full bg-amber-500 text-white px-1.5 py-0.5 leading-none tabular-nums"
                  >
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 p-2 rounded-lg">
            <Avatar className="w-9 h-9">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback>{user?.name?.[0] || "?"}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-md px-1.5 py-0.5 font-semibold ${roleBadge.cls}`}>
                {user?.role === "admin" && <ShieldCheck size={9} />}
                {roleBadge.label}
              </span>
            </div>
            <button
              data-testid="sidebar-logout"
              onClick={logout}
              className="text-slate-400 hover:text-slate-700 transition-colors"
              aria-label="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
